import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  addOutline,
  copyOutline,
  constructOutline,
  trashOutline,
  funnelOutline,
  searchOutline
} from 'ionicons/icons';
import { AppContextMenuService } from '../../services/app-context-menu.service';
import { AppContextMenuItem } from '../../components/app-context-menu/app-context-menu.models';
import { ComponentStudioService } from '../../services/component-studio.service';
import { StudioComponentDefinition } from '../../models/component-studio.models';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';

type ComponentLibrarySort =
  | 'updated-desc'
  | 'updated-asc'
  | 'name-asc'
  | 'name-desc'
  | 'usage-desc'
  | 'usage-asc';

@Component({
  selector: 'app-component-library-page',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, IonIcon, IonSpinner, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './component-library-page.component.html',
  styleUrls: ['./component-library-page.component.scss']
})
export class ComponentLibraryPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  readonly studioService = this.componentStudioService;

  searchTerm = '';
  sourceFilter = 'all';
  categoryFilter = 'all';
  statusFilter = 'all';
  sortBy: ComponentLibrarySort = 'updated-desc';

  constructor(
    private readonly componentStudioService: ComponentStudioService,
    private readonly contextMenuService: AppContextMenuService,
    private readonly router: Router,
    private readonly foregroundRefresh: ForegroundRefreshService
  ) {
    addIcons({
      addOutline,
      copyOutline,
      constructOutline,
      trashOutline,
      funnelOutline,
      searchOutline
    });
  }

  ngOnInit(): void {
    this.loadData();
    this.foregroundRefresh.watch(5000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadData(true));
  }

  loadData(force = false): void {
    this.componentStudioService.loadState(force).subscribe({
      error: () => {
        // The shared service exposes the error state for the template.
      }
    });
  }

  get categories(): string[] {
    return Array.from(new Set(this.componentStudioService.definitions().map((definition) => definition.category)));
  }

  get filteredDefinitions(): StudioComponentDefinition[] {
    const normalizedSearch = this.searchTerm.trim().toLowerCase();

    return this.componentStudioService.definitions().filter((definition) => {
      const matchesSearch = !normalizedSearch
        || definition.name.toLowerCase().includes(normalizedSearch)
        || definition.description.toLowerCase().includes(normalizedSearch)
        || definition.tags.some((tag) => tag.toLowerCase().includes(normalizedSearch));
      const matchesSource = this.sourceFilter === 'all' || definition.source === this.sourceFilter;
      const matchesCategory = this.categoryFilter === 'all' || definition.category === this.categoryFilter;
      const matchesStatus = this.statusFilter === 'all' || definition.status === this.statusFilter;

      return matchesSearch && matchesSource && matchesCategory && matchesStatus;
    }).sort((left, right) => this.compareDefinitions(left, right));
  }

  createComponent(): void {
    const componentId = this.componentStudioService.createBlankDefinition();
    this.openInBuilder(componentId);
  }

  duplicateDefinition(definitionId: string): void {
    this.contextMenuService.close();
    const duplicatedId = this.componentStudioService.duplicateDefinition(definitionId);
    if (duplicatedId) {
      this.openInBuilder(duplicatedId);
    }
  }

  deleteDefinition(definitionId: string): void {
    this.contextMenuService.close();
    const nextDefinitionId = this.componentStudioService.deleteDefinition(definitionId);
    const targetDefinitionId = nextDefinitionId ?? this.componentStudioService.createBlankDefinition();
    this.openInBuilder(targetDefinitionId);
  }

  openInBuilder(definitionId: string): void {
    this.contextMenuService.close();
    this.componentStudioService.setActiveDefinition(definitionId);
    this.router.navigate(['/component-builder'], {
      queryParams: { component: definitionId }
    });
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.sourceFilter = 'all';
    this.categoryFilter = 'all';
    this.statusFilter = 'all';
    this.sortBy = 'updated-desc';
  }

  previewLabels(definition: StudioComponentDefinition): string[] {
    return definition.root.children.slice(0, 3).map((node) => node.label);
  }

  openDefinitionContextMenu(event: MouseEvent, definition: StudioComponentDefinition): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuService.openAt(event, this.buildDefinitionContextMenuItems(definition), definition.name);
  }

  trackDefinition(_index: number, definition: StudioComponentDefinition): string {
    return definition.id;
  }

  canDeleteDefinition(definition: StudioComponentDefinition): boolean {
    return this.componentStudioService.canDeleteDefinition(definition);
  }

  sourceLabel(definition: StudioComponentDefinition): string {
    return definition.source === 'built-in' ? 'Built-in' : 'Custom';
  }

  private buildDefinitionContextMenuItems(definition: StudioComponentDefinition): AppContextMenuItem[] {
    if (definition.source === 'built-in') {
      return [
        {
          id: 'duplicate',
          label: 'Duplicate component',
          icon: 'copy-outline',
          action: () => this.duplicateDefinition(definition.id)
        }
      ];
    }

    const items: AppContextMenuItem[] = [
      {
        id: 'edit',
        label: 'Edit component',
        icon: 'construct-outline',
        action: () => this.openInBuilder(definition.id)
      },
      {
        id: 'duplicate',
        label: 'Duplicate component',
        icon: 'copy-outline',
        action: () => this.duplicateDefinition(definition.id)
      }
    ];

    items.push({
      id: 'delete',
      label: 'Delete component',
      icon: 'trash-outline',
      tone: 'danger',
      disabled: !this.canDeleteDefinition(definition),
      action: () => this.deleteDefinition(definition.id)
    });

    return items;
  }

  private compareDefinitions(left: StudioComponentDefinition, right: StudioComponentDefinition): number {
    switch (this.sortBy) {
      case 'updated-asc':
        return Date.parse(left.updatedAt) - Date.parse(right.updatedAt);
      case 'name-asc':
        return left.name.localeCompare(right.name);
      case 'name-desc':
        return right.name.localeCompare(left.name);
      case 'usage-desc':
        return right.usageCount - left.usageCount;
      case 'usage-asc':
        return left.usageCount - right.usageCount;
      case 'updated-desc':
      default:
        return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
    }
  }
}
